import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Briefcase, MapPin, ArrowRight, CheckCircle, AlertCircle, Search, Clock, Zap, ChevronDown } from 'lucide-react';
import { createClient } from '@supabase/supabase-js';

// --- SUPABASE CONFIG ---
const supabaseUrl = "https://jpanbtqxvxifpvadxhqb.supabase.co"; 
const supabaseKey = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImpwYW5idHF4dnhpZnB2YWR4aHFiIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzAwMDE5OTUsImV4cCI6MjA4NTU3Nzk5NX0.DzBmebHlkU0oI2p-ebep_cYugJYsegYzXbXJLHvtlLo";
const supabase = createClient(supabaseUrl, supabaseKey);

const Careers = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Tracks which job card is currently expanded
  const [expandedJobId, setExpandedJobId] = useState(null);
  
  const brandGold = "#ffb700";

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    const { data, error } = await supabase
      .from('job_postings')
      .select('*')
      .eq('is_active', true)
      .order('created_at', { ascending: false });
    
    if (data) setJobs(data);
    setLoading(false);
  };

  const filteredJobs = jobs.filter(job => 
    job.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleJob = (id) => {
    setExpandedJobId(expandedJobId === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-[#020408] text-white pt-32 px-4 md:px-12 relative z-10 pb-20 overflow-x-hidden">
      
      {/* Background Glows */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#ffb700]/5 blur-[120px] rounded-full pointer-events-none" />

      {/* Header Content */}
      <div className="max-w-7xl mx-auto text-center mb-12 relative z-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#ffb700]/30 bg-[#ffb700]/5 text-[#ffb700] text-[10px] md:text-xs font-bold mb-6 md:mb-8 uppercase tracking-widest backdrop-blur-md">
            <Zap size={12} className="text-[#ffb700]" /> Open Positions
          </div>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-black mb-6 tracking-tighter leading-[1.1]">
            Join The <br className="hidden md:block"/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#ffb700] to-orange-500">
              Intelligence Revolution
            </span>
          </h1>
          <p className="text-slate-400 text-base md:text-xl max-w-2xl mx-auto leading-relaxed px-4">
            We are building the intelligence layer for physical enterprise. 
            If you are obsessed with efficiency and autonomy, you belong here.
          </p>
        </motion.div>
      </div>

      {/* SEARCH BAR SECTION */}
      <div className="max-w-2xl mx-auto mb-16 relative z-10 px-2 md:px-0">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="relative group">
            <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
                <Search className="text-slate-500 group-focus-within:text-[#ffb700] transition-colors" size={20} />
            </div>
            <input 
                type="text"
                placeholder="Search for roles (e.g. AI Engineer)..."
                className="w-full bg-[#0F1218] border border-white/10 rounded-2xl py-4 pl-14 pr-4 text-white md:text-lg placeholder-slate-500 focus:outline-none focus:border-[#ffb700]/50 transition-all shadow-xl"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
            />
        </motion.div>
      </div>

      {/* Jobs List */}
      <div className="max-w-4xl mx-auto relative z-10">
        {loading ? (
          <div className="text-center flex flex-col items-center justify-center py-20">
             <div className="w-12 h-12 border-4 border-white/10 border-t-[#ffb700] rounded-full animate-spin mb-4" />
             <div className="text-[#ffb700] animate-pulse font-mono text-xs tracking-widest uppercase">Scanning Neural Network...</div>
          </div>
        ) : filteredJobs.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center p-10 md:p-16 border border-white/10 rounded-3xl md:rounded-[3rem] bg-[#0F1218] mx-2 md:mx-0">
            <AlertCircle className="mx-auto mb-6 text-slate-500" size={48} />
            <h3 className="text-xl md:text-2xl font-bold mb-2 text-white">
                {jobs.length === 0 ? "No Open Positions" : "No Matches Found"}
            </h3>
            <p className="text-slate-400 max-w-md mx-auto text-sm md:text-base">
                {jobs.length === 0 
                    ? "Our team is currently operating at full capacity. Please check back later." 
                    : `We couldn't find any roles matching "${searchQuery}". Try a different keyword.`}
            </p>
          </motion.div>
        ) : (
          <div className="flex flex-col gap-4 md:gap-6">
            {filteredJobs.map((job, i) => {
              const isExpanded = expandedJobId === job.id;

              return (
                <motion.div 
                  key={job.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className={`bg-[#0F1218] border rounded-2xl md:rounded-3xl transition-all duration-300 overflow-hidden ${
                    isExpanded ? 'border-[#ffb700]/50 shadow-[0_0_30px_rgba(255,183,0,0.1)]' : 'border-white/10 hover:border-white/30 hover:bg-white/5'
                  }`}
                >
                  {/* --- ALWAYS VISIBLE HEADER (Click to expand) --- */}
                  <div 
                    className="p-6 md:p-8 cursor-pointer flex flex-col gap-4 relative z-10"
                    onClick={() => toggleJob(job.id)}
                  >
                    
                    {/* Top Row: Title + Apply Now Button */}
                    <div className="flex justify-between items-start gap-4">
                      <h3 className="text-xl md:text-2xl font-black text-white group-hover:text-[#ffb700] transition-colors leading-tight">
                        {job.title}
                      </h3>
                      
                      {/* Apply Now Button at Top Right Corner */}
                      <a 
                        href={`mailto:careers@starkxai.com?subject=Application: ${job.title}`}
                        onClick={(e) => e.stopPropagation()} // Prevents accordion toggle
                        className="flex items-center gap-1.5 bg-white text-black font-black text-[10px] md:text-xs uppercase tracking-widest px-4 py-2 rounded-full hover:bg-[#ffb700] transition-all shadow-lg hover:shadow-[#ffb700]/20 shrink-0"
                      >
                        Apply <span className="hidden md:inline">Now</span> <ArrowRight size={14} className="ml-0.5" />
                      </a>
                    </div>

                    {/* Bottom Row: Tags + View Details */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-t border-white/5 md:border-none pt-4 md:pt-0">
                      
                      {/* Meta Tags */}
                      <div className="flex flex-wrap items-center gap-2 md:gap-3">
                          <div className="flex items-center gap-1.5 text-slate-300 font-mono text-[10px] md:text-xs uppercase tracking-widest bg-black/40 border border-white/10 px-3 py-1.5 rounded-full">
                            <MapPin size={12} className="text-[#ffb700]" /> {job.location}
                          </div>
                          
                          <div className="flex items-center gap-1.5 text-slate-300 font-mono text-[10px] md:text-xs uppercase tracking-widest bg-black/40 border border-white/10 px-3 py-1.5 rounded-full">
                            <Clock size={12} className="text-cyan-400"/> {job.experience || 'Exp N/A'}
                          </div>
                      </div>

                      {/* View Details / Chevron Dropdown */}
                      <div className="flex items-center justify-between w-full md:w-auto mt-2 md:mt-0 gap-3">
                        <span className="text-[10px] md:text-xs font-bold text-[#ffb700] uppercase tracking-widest">
                          {isExpanded ? 'Close Details' : 'View Details'}
                        </span>
                        <motion.div 
                          animate={{ rotate: isExpanded ? 180 : 0 }} 
                          transition={{ duration: 0.3 }}
                          className={`w-8 h-8 md:w-10 md:h-10 rounded-full shrink-0 flex items-center justify-center border transition-colors ${
                            isExpanded ? 'bg-[#ffb700] text-black border-[#ffb700]' : 'bg-transparent text-slate-400 border-white/10 group-hover:border-white/30'
                          }`}
                        >
                          <ChevronDown size={18} />
                        </motion.div>
                      </div>

                    </div>
                  </div>

                  {/* --- COLLAPSIBLE DETAILED CONTENT --- */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: "easeInOut" }}
                      >
                        <div className="px-6 md:px-8 pb-6 md:pb-8 border-t border-white/10 pt-6 bg-black/20">
                          
                          <p className="text-slate-300 mb-8 md:mb-10 text-sm md:text-base leading-relaxed">
                            {job.description}
                          </p>

                          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
                            <div>
                              <h4 className="font-bold text-white mb-4 uppercase tracking-wider text-[10px] md:text-xs flex items-center gap-2 bg-white/5 w-fit px-3 py-1.5 rounded-lg border border-white/5">
                                <Briefcase size={14} className="text-[#ffb700]" /> Responsibilities
                              </h4>
                              <p className="text-slate-400 text-xs md:text-sm whitespace-pre-line leading-relaxed font-medium">
                                {job.responsibilities}
                              </p>
                            </div>
                            <div>
                              <h4 className="font-bold text-white mb-4 uppercase tracking-wider text-[10px] md:text-xs flex items-center gap-2 bg-white/5 w-fit px-3 py-1.5 rounded-lg border border-white/5">
                                <CheckCircle size={14} className="text-[#ffb700]" /> Requirements
                              </h4>
                              <p className="text-slate-400 text-xs md:text-sm whitespace-pre-line leading-relaxed font-medium">
                                {job.eligibility}
                              </p>
                            </div>
                          </div>

                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Careers;